'use es6';

import { useState, useEffect } from 'react';
import { Map as ImmutableMap } from 'immutable';
import { RaiseError } from 'reporting-ui-components/trap/ErrorTrap';
import { RaiseLoading } from 'reporting-ui-components/trap/LoadingTrap';
import { Flex } from 'foundations-components/components/Flex';
import { addQuotaToDeals } from '../../../lib/goals/addGoalSeries';
import NoData from '../../../components/shared/NoData';
import empty from './empty';
import showDefaultNoData from './showDefaultNoData';
import { configure } from './configure';
import { loadResolve } from './loadResolve';
import { reportsEqual } from '../../../lib/shallowCompareReports';
import getErrorTypeFromException from 'reporting-data/lib/getErrorTypeFromException';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function DataDecorator(props) {
  const {
    report,
    setIsRendered,
    onError,
    runId,
    disableEmptyStates,
    errorRenderer,
    noDataRenderer,
    onReportResolve,
    runtimeOptions,
    Component
  } = props;
  const [configured, setConfigured] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const [hasSetRendered, setHasSetRendered] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  useEffect(() => {
    const resolveReport = reportToResolve => {
      if (!reportToResolve) {
        return;
      }
      loadResolve().then(({
        resolve: resolveFn
      }) => {
        Promise.all([configure(reportToResolve), resolveFn(reportToResolve, runtimeOptions)]).then(([configuredReport, resolvedData]) => {
          if (reportsEqual(reportToResolve, report)) {
            addQuotaToDeals(configuredReport, resolvedData).then(updatedReport => {
              if (isMounted) {
                setData(resolvedData);
                setError(null);
                setConfigured(updatedReport);
              }
              if (typeof onReportResolve === 'function') {
                onReportResolve({
                  data: resolvedData,
                  hasData: !empty(resolvedData, reportToResolve),
                  configured: updatedReport
                });
              }
            }).catch(caughtError => {
              if (onError) {
                onError({
                  type: getErrorTypeFromException(caughtError),
                  error: caughtError
                });
              } else {
                console.error(caughtError);
              }
              setError(caughtError || new Error('unknown error'));
              setData(null);
              return Promise.reject(caughtError);
            });
          }
        }).catch(caughtError => {
          if (onError) {
            onError({
              type: getErrorTypeFromException(caughtError),
              error: caughtError
            });
          } else {
            console.error(caughtError);
          }
          setError(caughtError || new Error('unknown error'));
          setData(null);
        });
      }).catch(caughtError => Promise.reject(caughtError));
    };
    setData(null);
    setError(null);
    resolveReport(report);
  }, [report, onError, runId, isMounted, onReportResolve, runtimeOptions]);
  const setRendered = () => {
    if (setIsRendered && !hasSetRendered) {
      setIsRendered();
      setHasSetRendered(true);
    }
  };
  const rhumbConfig = report.getIn(['displayParams', 'rhumbConfig'], ImmutableMap());
  const renderSuccessMarker = rhumbConfig.get('renderSuccessMarker');
  const renderErrorMarker = rhumbConfig.get('renderErrorMarker');
  if (error != null) {
    return errorRenderer ? errorRenderer(error, report) : /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      flexGrow: 1,
      "data-status-id": "report-status-error",
      children: [/*#__PURE__*/_jsx(RaiseError, {
        type: getErrorTypeFromException(error),
        error: error
      }), renderErrorMarker && renderErrorMarker()]
    });
  }
  if (!data) {
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      flexGrow: 1,
      "data-status-id": "report-status-loading",
      children: /*#__PURE__*/_jsx(RaiseLoading, {})
    });
  }
  if (!disableEmptyStates && empty(data, report)) {
    setRendered();
    if (noDataRenderer) {
      return noDataRenderer();
    }
    if (showDefaultNoData(report)) {
      return /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        flexGrow: 1,
        "data-status-id": "report-status-loaded",
        children: [/*#__PURE__*/_jsx(NoData, {}), renderSuccessMarker && renderSuccessMarker()]
      });
    }
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    flexGrow: 1,
    "data-status-id": "report-status-loaded",
    maxBlockSize: "100%",
    overflow: "auto",
    children: [/*#__PURE__*/_jsx(Component, Object.assign({}, props, {
      data: data,
      report: configured
    })), renderSuccessMarker && renderSuccessMarker()]
  });
}